import { ChartIcon, Grid2, NoData, Show } from "components";
import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  MFOrderTypeParam,
  useGetPortfolioAllocationQuery,
} from "store/features/MFOrders";
import EnterAmountFolioTable, {
  FolioWithHolding,
  PortfolioWithFolios,
} from "./EnterAmountFolioTable";
import PortfolioSelectionTable, {
  PortfolioRow,
} from "./PortfolioSelectionTable";
import {
  MFOrderFormValues,
  getInitialMFOrderItem,
  useWatchFields,
} from "./useMFOrders";

interface ApiSchemeFolio {
  folioNo: string;
  quantity: number;
  investedAmount: number;
  currentValue: number;
  cmp: number;
  isSipActive?: boolean;
}

const toFolio = (f: ApiSchemeFolio): FolioWithHolding => ({
  folioNumber: f.folioNo,
  schemeHolding: f.currentValue,
  holdingQty: f.quantity,
  investedAmount: f.investedAmount,
  nav: f.cmp,
  isSipActive: f.isSipActive,
});

const FormDetails = () => {
  const { "extraThings.rightVisibleTable": watchRightVisibleTable } =
    useWatchFields();
  const { control, setValue, getValues } = useFormContext<MFOrderFormValues>();
  const gcode = useWatch({ control, name: "gcode" });
  const orderType = useWatch({ control, name: "orderType" });
  const items = useWatch({ control, name: "items" }) || [];
  const portfolioFilter = useWatch({
    control,
    name: "portfolioSelectionFilter",
  });

  const handleAddFolio = (portfolioId: number) => {
    const current = getValues("items") ?? [];
    setValue(
      "items",
      [
        ...current,
        { ...getInitialMFOrderItem(), portfolioId, folioNumber: null },
      ],
      { shouldDirty: true },
    );
  };

  const handleDeleteFolio = (itemIndex: number) => {
    const current = getValues("items") ?? [];
    setValue(
      "items",
      current.filter((_, idx) => idx !== itemIndex),
      { shouldDirty: true },
    );
  };

  const canFetch = !!gcode && !!orderType;
  const { data: portfolioResponse, isFetching: isPortfoliosLoading } =
    useGetPortfolioAllocationQuery(
      {
        gcode,
        orderType: orderType as MFOrderTypeParam,
        portfolio_group: portfolioFilter?.portfolio_group,
        portfolio_value: portfolioFilter?.portfolio_value,
      },
      { skip: !canFetch },
    );

  const apiPortfolios = portfolioResponse?.data?.results;

  // Redemption / Switch act on existing holdings, so only portfolios that
  // actually hold the scheme (have folio entries) are eligible. Purchase can
  // target any portfolio, so all are shown.
  const isHoldingsBased = orderType === "redemption" || orderType === "switch";

  const portfolios = useMemo<PortfolioRow[]>(() => {
    if (!apiPortfolios) return [];
    return (
      apiPortfolios as unknown as Array<
        PortfolioRow & {
          schemeFolios?: ApiSchemeFolio[];
        }
      >
    )
      .map((p) => ({
        portfolioId: p.portfolioId,
        clientName: p.clientName,
        portfolioCode: p.portfolioCode,
        mfReadinessStatus: p.mfReadinessStatus,
        currentAmount: p.currentAmount,
        portfolioValue: p.portfolioValue,
        schemeFolios: (p.schemeFolios ?? []).map(toFolio),
      }))
      .filter((p) =>
        isHoldingsBased ? (p.schemeFolios?.length ?? 0) > 0 : true,
      );
  }, [apiPortfolios, isHoldingsBased]);

  const selectedPortfolios: PortfolioWithFolios[] = useMemo(() => {
    const selectedIds = new Set(
      items
        .map((item) => item.portfolioId)
        .filter((id): id is number => id != null),
    );
    return portfolios
      .filter((row) => selectedIds.has(row.portfolioId))
      .map((row) => ({
        portfolioId: row.portfolioId,
        clientName: row.clientName,
        portfolioCode: row.portfolioCode,
        portfolioValue: row.portfolioValue,
        folios: row.schemeFolios ?? [],
      }));
  }, [items, portfolios]);

  return (
    <Grid2 container size={12}>
      <Show.When isTrue={watchRightVisibleTable === null}>
        <Grid2
          size={12}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="calc(100vh - 174px)"
        >
          <NoData
            text="No Purchase, Redemption or Switch done yet."
            subText="Please fill up the form to proceed forward"
            customImg={
              <ChartIcon height={100} width={100} color="var(--neutral-100)" />
            }
          />
        </Grid2>
      </Show.When>

      <Show.When isTrue={watchRightVisibleTable === "SELECT_PORTFOLIO"}>
        <Grid2 size={12}>
          <PortfolioSelectionTable
            portfolios={portfolios}
            loading={isPortfoliosLoading}
          />
        </Grid2>
      </Show.When>

      <Show.When isTrue={watchRightVisibleTable === "ENTER_AMOUNT_FOLIO"}>
        <Grid2 size={12}>
          <EnterAmountFolioTable
            portfolios={selectedPortfolios}
            onAddFolio={handleAddFolio}
            onDeleteFolio={handleDeleteFolio}
          />
        </Grid2>
      </Show.When>

      <Show.When isTrue={watchRightVisibleTable === "PLACE_ORDER"}>
        <Grid2 size={12}>
          <EnterAmountFolioTable portfolios={selectedPortfolios} readOnly />
        </Grid2>
      </Show.When>
    </Grid2>
  );
};

export default FormDetails;
